import { initAxios } from '../../../utils/axiosHelper';
import {
  DatabasePaginationParams,
  IDatabase,
  IListItem,
  PaginatedResponse,
} from './database';

// Helper to check and re-throw auth errors
const handleServiceError = (err: any) => {
  const status = err?.response?.status;
  if (status === 401 || status === 403) {
    console.error('[SERVICE] Auth error detected, re-throwing:', status);
    throw err;
  }
};

const defaultPagination = {
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 10,
  hasNextPage: false,
  hasPreviousPage: false,
};

export const getDatabasesByGroupId = async (
  id?: string,
): Promise<IDatabase[]> => {
  try {
    if (!id) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(`/bite-db/database-group/${id}`);
    return resp.data.data;
  } catch (err) {
    handleServiceError(err);
    return [];
  }
};

export const getDatabaseListItemsByGroupId = async (
  id?: string,
  searchType?: string,
): Promise<IListItem[]> => {
  try {
    if (!id) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const queryParams = new URLSearchParams({
      type: searchType || '',
    });

    const resp = await instance.get(
      `/bite-db/database-group/${id}/list-items?${queryParams.toString()}`,
    );
    return resp.data.data;
  } catch (err) {
    handleServiceError(err);
    return [];
  }
};

export const getDatabasesByGroupIdPagination = async (
  id?: string,
  params?: Partial<DatabasePaginationParams>,
): Promise<PaginatedResponse<IDatabase>> => {
  try {
    if (!id) {
      return { data: [], pagination: defaultPagination };
    }
    const instance = await initAxios();
    if (!instance) {
      return { data: [], pagination: defaultPagination };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'created_at',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      subtype: params?.subtype || '',
      type: params?.type || '',
      database_structure: params?.database_structure || '',
    });

    const resp = await instance.get(
      `/bite-db/databases/group-id/${id}?${queryParams.toString()}`,
    );
    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || defaultPagination,
    };
  } catch (err) {
    handleServiceError(err);
    return { data: [], pagination: defaultPagination };
  }
};

export const getDatabaseById = async (
  id?: string,
): Promise<IDatabase | null> => {
  try {
    if (!id) {
      return null;
    }
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/database/${id}`);
    return resp.data?.data || null;
  } catch (err) {
    handleServiceError(err);
    return null;
  }
};

export const deleteDatabase = async (
  id: string,
): Promise<{ success: boolean; message?: string }> => {
  try {
    if (!id) {
      return { success: false, message: 'Database ID is required' };
    }
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.delete(`/bite-db/database/${id}`);
    return {
      success: true,
      message: resp.data?.message || 'Database deleted successfully',
    };
  } catch (err: any) {
    handleServiceError(err);
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to delete database',
    };
  }
};

export const createDatabase = async (
  database: Partial<IDatabase>,
): Promise<{ success: boolean; result?: any; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.post('/bite-db/database', database);
    return { success: true, result: resp.data };
  } catch (err: any) {
    handleServiceError(err);
    return {
      success: false,
      result: err.response?.data?.message || 'Failed to create database',
      message: err.response?.data?.message || 'Failed to create database',
    };
  }
};

export const bulkUploadSuppliers = async (
  formData: FormData,
): Promise<{ success: boolean; result?: any; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    // Remove Content-Type header to let browser set it with boundary for multipart/form-data
    delete instance.defaults.headers.common['Content-Type'];
    delete instance.defaults.headers.post['Content-Type'];
    delete instance.defaults.headers['Content-Type'];

    const resp = await instance.post(
      '/bite-db/add-supplier-bulk-upload',
      formData,
    );
    return { success: true, result: resp.data };
  } catch (err: any) {
    return {
      success: false,
      result: err.response?.data?.message || 'Failed to upload suppliers',
      message: err.response?.data?.message || 'Failed to upload suppliers',
    };
  }
};

export const bulkUploadTariffs = async (
  formData: FormData,
): Promise<{ success: boolean; result?: any; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    // Remove Content-Type header to let browser set it with boundary for multipart/form-data
    delete instance.defaults.headers.common['Content-Type'];
    delete instance.defaults.headers.post['Content-Type'];
    delete instance.defaults.headers['Content-Type'];

    const resp = await instance.post(
      '/bite-db/add-tariff-bulk-upload',
      formData,
    );
    return { success: true, result: resp.data };
  } catch (err: any) {
    return {
      success: false,
      result:
        err.response?.data?.message || 'Failed to upload tariff transactions',
      message:
        err.response?.data?.message || 'Failed to upload tariff transactions',
    };
  }
};

export const bulkUploadProducts = async (
  formData: FormData,
): Promise<{ success: boolean; result?: any; message?: string }> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    // Remove Content-Type header to let browser set it with boundary for multipart/form-data
    delete instance.defaults.headers.common['Content-Type'];
    delete instance.defaults.headers.post['Content-Type'];
    delete instance.defaults.headers['Content-Type'];

    const resp = await instance.post(
      '/bite-db/add-product-bulk-upload',
      formData,
    );
    return { success: true, result: resp.data };
  } catch (err: any) {
    return {
      success: false,
      result: err.response?.data?.message || 'Failed to upload products',
      message: err.response?.data?.message || 'Failed to upload products',
    };
  }
};

export const updateDatabase = async (
  id: string,
  database: Partial<IDatabase>,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    if (!id) {
      return { success: false, message: 'Database ID is required' };
    }
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(`/bite-db/database/${id}`, database);
    return {
      success: true,
      data: resp.data?.data,
      message: resp.data?.message || 'Database updated successfully',
    };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to update database',
    };
  }
};

export const archiveDatabase = async (
  id: string,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    if (!id) {
      return { success: false, message: 'Database ID is required' };
    }
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(`/bite-db/database/${id}/archive`);
    return {
      success: true,
      data: resp.data?.data,
      message: resp.data?.message || 'Database archived successfully',
    };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to archive database',
    };
  }
};

export const restoreDatabase = async (
  id: string,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    if (!id) {
      return { success: false, message: 'Database ID is required' };
    }
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(`/bite-db/database/${id}/restore`);
    return {
      success: true,
      data: resp.data?.data,
      message: resp.data?.message || 'Database restored successfully',
    };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to restore database',
    };
  }
};

export const filterDatabases = (
  databases: IDatabase[],
  searchTerm: string,
): IDatabase[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return databases;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return databases.filter((db) => {
    return (
      db.name?.toLowerCase().includes(lowerSearchTerm) ||
      db.description?.toLowerCase().includes(lowerSearchTerm) ||
      db.type?.toLowerCase().includes(lowerSearchTerm)
    );
  });
};
